import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTracking } from 'react-tracking';
import type { ReactNode, ReactElement } from 'react';
import loadable from '@loadable/component';
import {
  Wrapper,
  ActionsPlaceHolder,
  DefaultOverlay,
  Container,
  ColorOverlay,
} from './styled';
import Observer from '@researchgate/react-intersection-observer';
import { intersectorThresholds } from '@/constants/intersectorThresholds';
import { getViewMode } from '@/redux/location/selectors';
import { useSelector } from 'react-redux';
import { ViewMode } from '@/pages/CompanyReport/enum';
import useUser from '@/hooks/useUser';
import { useFlag } from '@/features/unleash';
import ChartRegistrationOverlay from './components/ChartRegistrationOverlay';

const ChartActions = loadable(
  () =>
    import('@/pages/CompanyReport/components/Overview/components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActions,
    fallback: <ActionsPlaceHolder />,
  }
);

const RawDataWithRegoModal = loadable(
  () => import('./components/RawDataWithRegoModal'),
  {
    ssr: false,
  }
);

interface ChartWrapperProps {
  chart?: ReactElement;
  table?: ReactNode;
  rawData?: ReactNode;
  rawDataModal?: ReactNode;
  rawDataTitle?: string;
  id?: string;
  companyName?: string;
  helpTitle?: string;
  help?: ReactNode;
  feedbackLabel?: string;
  exportLightUrl?: string;
  exportDarkUrl?: string | null;
  keepChartRendered?: boolean;
  actionsMarginTop?: number;
  preRenderHeight?: number | string;
  preRenderHeightMdDown?: number | string;
  preRenderHeightSmDown?: number | string;
  moreActions?: ReactNode;
  defaultModeText?: string;
  hideDataButton?: boolean;
  blurIfUnauthenticated?: boolean;
}
export interface ChartWrapperRef {
  showChart: (show: boolean) => void;
}

/**
 * This component initially server side renders the table provided, then on intersection will render the chart provided
 *
 * Note: Don't need to wrap the chart with DeferRenderUntilIntersecting as its handled internally here
 */
const ChartWrapper = forwardRef<ChartWrapperRef, ChartWrapperProps>(
  (props, ref) => {
    const {
      chart,
      table,
      rawData,
      rawDataModal,
      rawDataTitle,
      id,
      companyName,
      helpTitle,
      help,
      feedbackLabel,
      exportLightUrl,
      exportDarkUrl,
      keepChartRendered = true,
      actionsMarginTop,
      preRenderHeight,
      preRenderHeightMdDown,
      preRenderHeightSmDown,
      moreActions,
      defaultModeText,
      hideDataButton,
      blurIfUnauthenticated = true,
    } = props;

    const [showTable, setShowTable] = useState<boolean>(true);
    const [showRawData, setShowRawData] = useState<boolean>(false);
    const [observed, setObserved] = useState<boolean>(false);
    const { trackEvent } = useTracking();
    const { data: userData } = useUser();
    const flag = useFlag({ name: 'mono-inline-rego' });
    const shouldEnableInlineRego =
      flag.enabled &&
      flag.flagsReady &&
      flag.variant.name === 'default-inline-rego';
    const shouldBlurChart =
      !userData.loggedIn && shouldEnableInlineRego && blurIfUnauthenticated;
    const toggleData = () => {
      if (rawData) {
        setShowRawData((showRawData) => !showRawData);
      } else {
        trackEvent({
          id,
          action: 'click',
          modifier: 'toggleData',
          subject: showTable ? 'showChart' : 'showTable',
        });
        setShowTable((showTable) => !showTable);
      }
    };
    const viewMode = useSelector(getViewMode);

    useEffect(() => {
      if (viewMode === ViewMode.Print) {
        setObserved(true);
        setShowTable(false);
      }
    }, [viewMode]);

    useImperativeHandle(ref, () => ({
      showChart: (show) => {
        rawData ? setShowRawData(!show) : setShowTable(!show);
      },
    }));

    return (
      <>
        {/* Chart */}
        {keepChartRendered ? (
          <Container>
            <Wrapper
              data-cy-id={`${id}-wrapper`}
              showDefaultMode={defaultModeText !== undefined}
              hidden={showTable}
              height={preRenderHeight}
              heightMdDown={preRenderHeightMdDown}
              heightSmDown={preRenderHeightSmDown}
              shouldBlur={shouldBlurChart}
            >
              {observed && chart}
              {!showTable && shouldBlurChart && (
                <ColorOverlay data-cy-id={`${id}-colour-overlay`} />
              )}
            </Wrapper>
            {defaultModeText !== undefined && (
              <DefaultOverlay
                data-cy-id={`${id}-default`}
                justifyContent="center"
                alignItem="center"
              >
                {defaultModeText}
              </DefaultOverlay>
            )}
            {/* Overlay for inline rego experiment */}
            {!showTable && shouldBlurChart && (
              <ChartRegistrationOverlay id={id} />
            )}
          </Container>
        ) : (
          !showTable && (
            <Container>
              <Wrapper
                data-cy-id={`${id}-wrapper`}
                height={preRenderHeight}
                heightMdDown={preRenderHeightMdDown}
                heightSmDown={preRenderHeightSmDown}
                shouldBlur={shouldBlurChart}
              >
                {chart}
                {shouldBlurChart && (
                  <ColorOverlay data-cy-id={`${id}-colour-overlay`} />
                )}
              </Wrapper>
              {/* Overlay for inline rego experiment */}
              {shouldBlurChart && <ChartRegistrationOverlay id={id} />}
            </Container>
          )
        )}
        {/* Modal Data */}
        {showRawData && (
          <RawDataWithRegoModal
            rawData={rawData}
            setShowRawData={setShowRawData}
            showRegoModal={shouldBlurChart}
          />
        )}
        <Observer
          disabled={observed}
          threshold={intersectorThresholds}
          rootMargin="600px 0px"
          onChange={({ isIntersecting }) => {
            if (isIntersecting) {
              setObserved(true);
              setShowTable(false);
            }
          }}
        >
          {/* Inline data table */}
          <Container>
            <Wrapper
              data-cy-id={`${id}-wrapper`}
              hidden={!showTable}
              scrollOnOverflow={showTable}
              height={preRenderHeight}
              heightMdDown={preRenderHeightMdDown}
              heightSmDown={preRenderHeightSmDown}
              shouldBlur={shouldBlurChart}
            >
              {showTable && table}
              {showTable && shouldBlurChart && (
                <ColorOverlay data-cy-id={`${id}-colour-overlay`} />
              )}
            </Wrapper>
            {/* Overlay for inline rego experiment */}
            {showTable && shouldBlurChart && (
              <ChartRegistrationOverlay id={id} />
            )}
          </Container>
        </Observer>
        {observed && (moreActions || help || feedbackLabel || table) ? (
          <ChartActions
            id={id}
            companyName={companyName}
            helpTitle={helpTitle}
            help={help}
            feedbackLabel={feedbackLabel}
            exportLightUrl={exportLightUrl}
            exportDarkUrl={exportDarkUrl}
            toggleDataActive={showTable}
            onToggleData={toggleData}
            showDataButton={
              !hideDataButton && (table !== undefined || rawData !== undefined)
            }
            actionsMarginTop={actionsMarginTop}
            moreActions={moreActions}
            rawData={rawDataModal}
            rawDataTitle={rawDataTitle}
            shouldShowRegoModal={shouldBlurChart}
          />
        ) : (
          <ActionsPlaceHolder />
        )}
      </>
    );
  }
);

export default ChartWrapper;
